import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  darken,
  lighten,
  alpha,
} from "@mui/material";
import { useSelector } from "react-redux";
import MetricCell from "./components/MetricCell";
import DetailModal from "./components/DetailModal";
import { withTranslationWrapper } from "../../HOC";
import CustomTextField from "../../Components/common/TextField";
import CustomDateRangePicker from "../../Components/common/DateRangeHistoryPicker";
import moment from "moment";
import instance from "../../axios";
import Loader from "../../Layout/Loader";
import MenuItem from "../../Components/common/MenuItem";

const metrics = [
  { name: "Productivity", benchmark: 80 },
  { name: "Utilization", benchmark: 70 },
  { name: "Utilization & Productivity", benchmark: 70 },
  { name: "Avg Idle", benchmark: 70 },
];



const transformApiData = (apiData = {}) => {
  return Object.entries(apiData).map(([key, value], index) => {
    const toTrend = (diff) => {
      if (diff > 0) return "up";
      if (diff < 0) return "down";
      return "constant";
    };

    return {
      id: index,
      name: key.charAt(0).toUpperCase() + key.slice(1),
      icon: `/assets/category/default/${"default"}.svg`,
      count: value?.count||1,

      metrics: {
        Productivity: {
          value: Number(value.productivityPercentage || 0),
          benchmark: Number(value.productivityBenchmark || 0),
          diff:
            Number(value.productivityPercentage || 0) -
            Number(value.productivityBenchmark || 0),
          trend: toTrend(
            Number(value.productivityPercentage || 0) -
            Number(value.productivityBenchmark || 0)
          ),
        },

        Utilization: {
          value: Number(value.utilizationPercentage || 0),
          benchmark: Number(value.utilizationBenchmark || 0),
          diff:
            Number(value.utilizationPercentage || 0) -
            Number(value.utilizationBenchmark || 0),
          trend: toTrend(
            Number(value.utilizationPercentage || 0) -
            Number(value.utilizationBenchmark || 0)
          ),
        },

        "Utilization & Productivity": {
          value: Number(value.utilizationAndProductivity || 0),
          benchmark: Number(value.utilizationAndProductivityBenchmark || 0),
          diff:
            Number(value.utilizationAndProductivity || 0) -
            Number(value.utilizationAndProductivityBenchmark || 0),
          trend: toTrend(
            Number(value.utilizationAndProductivity || 0) -
            Number(value.utilizationAndProductivityBenchmark || 0)
          ),
        },

        "Avg Idle": {
          value: value.averageIdling || "0 MIN",
          benchmark: value.averageIdlingBenchmark || "",
          diff: 0,
          trend: "constant",
        },
      },
    };
  });
};
const options = [
  { id: 1, name: "Work",value:"workMode" },
  { id: 2, name: "Motion",value:"default" },
  // { id: 3, name: "Service",value:'service' },
];
const ProductivityDashboard = (props) => {
  const themecolors = useSelector((state) => state.themeColors);
  const logInUser = useSelector((state) => state.logInUsers);
  const ServerSetting = useSelector((state) => state.ServerSetting);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [evaluationSource, setEvaluationSource] = useState("workMode");
const [date, setDate] = useState(() => {
  let timezone = "Asia/Dubai";
  if (ServerSetting?.attributes?.timezone) timezone = ServerSetting.attributes.timezone;
  if (logInUser?.attributes?.timezone) timezone = logInUser.attributes.timezone;

  const timeFormat = logInUser?.twelveHourFormat ? "hh:mm A" : "HH:mm";

const end = moment.tz(timezone).subtract(1, "days").endOf("day");
const start = moment.tz(timezone).subtract(30, "days").startOf("day");

const fromDate = start.clone().utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
const toDate = end.clone().utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
  const selectedDate = `${start.format("YYYY-MM-DD " + timeFormat)} - ${end.format("YYYY-MM-DD " + timeFormat)}`;

  return {
    selectedDate,
    fromDate,
    toDate,
  };
});




const fetchProductivityData = async (fromDate, toDate, signal) => {
  try {
    const { data } = await instance.get(
      "/api/dashboards/graph/utilization-productivity",
      {
        params: { from: fromDate, to: toDate,evaluationSource },
        signal,
      }
    );
    return data;
  } catch (error) {
    if (error.name === "CanceledError") {
      return null;
    }
    console.error("API call failed:", error);
    return null;
  }
};


useEffect(() => {
  const controller = new AbortController();

  const fetchData = async () => {
    setIsLoading(true);
    setData([]);

    const response = await fetchProductivityData(
      date.fromDate,
      date.toDate,
      controller.signal
    );

    if (!controller.signal.aborted) {
      if (response) {
        setData(transformApiData(response));
      } else {
        setData([]);
      }
      setIsLoading(false);
    }
  };

  fetchData();

  return () => {
    controller.abort();
  };
}, [date.fromDate, date.toDate,evaluationSource]);



  const filteredData = useMemo(() => {
    if (!search.trim()) return data;
    return data.filter((category) =>
      category.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [data, search]);

  // const totalUnits = useMemo(
  //   () => filteredData.reduce((sum, cat) => sum + cat.count, 0),
  //   [filteredData]
  // );

  const handleCategoryClick = (category) => {
    setSelectedData({
      category,
      metrics: category.metrics,
    });
    setModalOpen(true);
  };

  const handleDateRangeApply = (event, picker) => {
    const startDate = picker.startDate;
    const endDate = picker.endDate;


    let timezone = 'Asia/Dubai';
    if (ServerSetting?.attributes?.timezone) {
      timezone = ServerSetting.attributes.timezone;
    }
    if (logInUser?.attributes?.timezone) {
      timezone = logInUser.attributes.timezone;
    }
    moment.tz.setDefault(timezone);
    const timeFormat = logInUser?.twelveHourFormat ? 'hh:mm A' : 'HH:mm';
    let timevalue = `${moment(startDate).format('YYYY-MM-DD ' + timeFormat)} - ${moment(endDate).format('YYYY-MM-DD ' + timeFormat)}`
    const startDateUtc = moment(startDate).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    const endDateUtc = moment(endDate).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    setDate((prevState) => ({
      ...prevState,
      selectedDate: timevalue,
      fromDate: startDateUtc,
      toDate: endDateUtc
    }))
  };
    const handleChange =(event) => {
    const value = event.target.value;
    setEvaluationSource(value);
  };
const timezone = logInUser?.attributes?.timezone || ServerSetting?.attributes?.timezone || "Asia/Dubai";
  return (
    <Box sx={{ height: "100vh", p: { xs: 0, sm: 0 } }}>
      <Box sx={{ display: "flex", gap: { xs: 0.5, sm: 1 }, height: "100%" }}>
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: themecolors?.backgroundColor || "#045266",
              color: themecolors?.textColor || "white",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: { xs: 0.5, sm: 1 },
            }}
          >
            <Box>
              <CustomTextField
                label={"Search"}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
            <Box sx={{ minWidth: "165px" }}>
                  <CustomTextField
      id="evaluationSource"
      select
      label={"Evaluation Source"}
      value={evaluationSource}
      onChange={handleChange}
      SelectProps={{
        MenuProps: {
          className: props.classes?.menu,
        },
      }}
      margin="dense"
      fullWidth
    >
      {/* <MenuItem value={0}>
        <em>None</em>
      </MenuItem> */}
      {options.map((option, index) => (
        <MenuItem key={index} value={option.value}>
          {props.translate(option.name)}
        </MenuItem>
      ))}
    </CustomTextField>
</Box>
            <Box sx={{ minWidth: "165px" }}>
              <CustomDateRangePicker
                fill
  startDate={moment.tz(date.fromDate, timezone)}
  endDate={moment.tz(date.toDate, timezone)}
                onEvent={handleDateRangeApply}
                selectedDate={date.selectedDate || ""}
                label="Select Date Range"
              />
            </Box>
          </Paper>
          <TableContainer
            sx={{
              height: "fit-content",
              borderTopLeftRadius: { xs: 4, sm: 8 },
              borderTopRightRadius: { xs: 4, sm: 8 },
              border: `1px solid ${themecolors?.backgroundColor || "#045266"}`,
              maxHeight: "100%",
              bgcolor: themecolors?.menuActiveBackground || "#0a83a1",
              color: themecolors?.textColor || "white",
              overflow: "auto",
              "&::-webkit-scrollbar": { width: "8px", height: "8px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: themecolors?.menuActiveBackground || "#0a83a1",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: themecolors?.themeDarkColor || "#045266",
                borderRadius: "8px",
                border: `2px solid ${themecolors?.menuActiveBackground || "#0a83a1"}`,
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: darken(themecolors?.themeDarkColor || "#045266", 0.3),
              },
            }}
          >
            <Table stickyHeader sx={{ minWidth: { xs: 500, sm: 700 } }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      top: 0,
                      bgcolor: themecolors?.backgroundColor || "#045266",
                      color: themecolors?.textColor || "white",
                      fontWeight: 600,
                      fontSize: { xs: 14, sm: 16, md: 18 },
                      textAlign: "center",
                      borderBottom: `1px solid ${alpha(themecolors?.textColor || "#045266", 0.2)}`,
                      maxWidth: { xs: 100, sm: 200, md: 250 },
                      px: { xs: 1, sm: 2 },
                      py: { xs: 1.5, sm: 2 },
                      zIndex: 11,
                    }}
                  >
                    Categories ({filteredData?.length})
                  </TableCell>

                  {metrics.map((metric, idx) => (
                    <TableCell
                      key={idx}
                      sx={{
                        position: "sticky",
                        top: 0,
                        bgcolor: themecolors?.backgroundColor || "#045266",
                        color: themecolors?.textColor || "white",
                        fontWeight: 600,
                        fontSize: { xs: 14, sm: 16, md: 18 },
                        textAlign: "center",
                        borderBottom: `1px solid ${alpha(themecolors?.textColor || "#045266", 0.2)}`,
                        minWidth: { xs: 180, sm: 240 },
                        px: { xs: 1, sm: 2 },
                        py: { xs: 1.5, sm: 2 },
                        zIndex: 10,
                      }}
                    >
                      {metric.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredData?.length === 0 && isLoading && (
                  <TableRow>
                    <TableCell colSpan={metrics.length + 1} align="center">
                      <Loader />
                    </TableCell>
                  </TableRow>
                )}

                {filteredData.length === 0 && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={metrics.length + 1} align="center">
                      <Typography sx={{ color: "white", py: 2 }}>
                        No data found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {filteredData.map((category, idx) => (
                  <TableRow key={category.id}>
                    <TableCell
                      onClick={() => handleCategoryClick(category)}
                      sx={{
                        position: "sticky",
                        left: 0,
                        bgcolor: lighten(themecolors?.backgroundColor || "#045266", 0.05),
                        borderBottom: `1px solid ${alpha(themecolors?.textColor || "#045266", 0.2)}`,
                        borderRight: `1px solid ${alpha(themecolors?.textColor || "#045266", 0.2)}`,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        p: { xs: 1.5, sm: 2 },
                        maxWidth: { xs: 150, sm: 200, md: 250 },
                        zIndex: 1,
                        "&:hover": {
                          bgcolor: lighten(themecolors?.backgroundColor || "#045266", 0.1),
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} sx={{ width: "100%" }}>
                        <img src={category.icon} alt={category.name} width={50} style={{ maxWidth: "40px" }} />
                        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                          <Typography
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: { xs: 14, sm: 16, md: 18 },
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: themecolors?.textColor || "white",
                            }}
                          >
                            {category.name}
                          </Typography>
                          <Typography
                            sx={{
                              ml: 0.5,
                              fontSize: { xs: 14, sm: 16, md: 18 },
                              fontWeight: 600,
                              flexShrink: 0,
                              color: themecolors?.textColor || "white",
                            }}
                          >
                            ({category.count})
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {metrics.map((metric, index) => (
                      <TableCell
                        key={metric.name}
                        sx={{
                          bgcolor:
                            idx % 2 === 0
                              ? lighten(themecolors?.menuActiveBackground, 0.1)
                              : darken(themecolors?.menuActiveBackground, 0.1),
                          borderBottom:
                            idx === filteredData.length - 1
                              ? "none"
                              : `1px solid ${alpha(themecolors?.textColor || "#045266", 0.2)}`,
                          borderRight:
                            index === metrics.length - 1
                              ? "none"
                              : `1px solid ${alpha(themecolors?.textColor || "#045266", 0.2)}`,
                          p: 0,
                        }}
                      >
                        <MetricCell metric={category.metrics[metric.name]} themecolors={themecolors} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {isLoading && filteredData.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={metrics.length + 1} align="center">
                      <Loader />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <DetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        data={selectedData}
        themecolors={themecolors}
      />
    </Box>
  );
};

export default withTranslationWrapper(ProductivityDashboard);
